import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { BulkResponse, BulkResultItem } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

import { productTitles } from './product-titles';

export interface BulkResultDialogProps {
  result: BulkResponse | null;
  /** Labels for the ids sent; an id without one is shown through `fallbackTitle`. */
  titles: ReadonlyMap<string, string>;
  fallbackTitle: (id: string) => ReactNode;
  /**
   * The ids are master products: titles missing from `titles` are looked up by id (the failures
   * of a selection, "all matching", are mostly rows that never loaded).
   */
  lookupProductTitles?: boolean;
  /** Introduces the list, e.g. "Products that could not be changed, with the reason:". */
  description: string;
  /** More about one refused row under its reason, like a link to what refused it. */
  detail?: (item: BulkResultItem) => ReactNode;
  onClose: () => void;
}

/**
 * The per-row outcome of a 207 from any `BulkResponse` endpoint: which records were not changed
 * and why, in the order sent. The message is the one a single request would have shown. A
 * selection's answer (`matched` set, failures only) reads "1,240 updated · 3 failed".
 */
export function BulkResultDialog({
  result,
  titles,
  fallbackTitle,
  lookupProductTitles = false,
  description,
  detail,
  onClose,
}: BulkResultDialogProps) {
  const { t, i18n } = useTranslation(['common', 'errors']);
  const failed = result?.items.filter((item) => item.status >= 300) ?? [];
  const missing = failed.map((item) => item.id).filter((id) => !titles.has(id));
  const resolved = useQuery({
    queryKey: ['bulk-result-titles', missing],
    queryFn: () => productTitles(missing),
    enabled: lookupProductTitles && missing.length > 0,
    staleTime: Infinity,
    meta: { silentError: true },
  });
  const titleOf = (id: string) => titles.get(id) ?? resolved.data?.get(id) ?? fallbackTitle(id);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <Dialog open={result !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {result?.matched === undefined
              ? t('common:bulkResult.title', {
                  succeeded: result?.succeeded ?? 0,
                  failed: result?.failed ?? 0,
                })
              : t('common:bulkResult.matchedTitle', {
                  succeeded: number.format(result.succeeded),
                  failed: number.format(result.failed),
                })}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <ul className="divide-y rounded-md border" aria-label={description}>
          {failed.map((item) => (
            <li key={item.id} className="flex flex-col gap-0.5 px-3 py-2 text-sm">
              <span className="font-medium">{titleOf(item.id)}</span>
              {item.code ? (
                <span className="text-destructive text-xs">{t(`errors:code.${item.code}`)}</span>
              ) : null}
              {item.message ? (
                <span className="text-muted-foreground text-xs">{item.message}</span>
              ) : null}
              {detail?.(item)}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
